import * as THREE from 'three';

export type PhotonHeatmapNormalizationMode = 'raw' | 'percentile' | 'fixed';

/**
 * Fixed irradiance scale (W/m²) used in 'fixed' normalization mode.
 * 0 = deep shadow/indoor, 800 = peak direct outdoor summer sun.
 */
export const HEATMAP_FIXED_SCALE_MAX_WM2 = 800;

export interface PhotonHeatmapLegendStats {
	enabled: boolean;
	cellCount: number;
	minEnergy: number;
	maxEnergy: number;
	cellSize: number;
	normalizationMode: PhotonHeatmapNormalizationMode;
	displayMinEnergy: number;
	displayMaxEnergy: number;
}

export interface PhotonHeatmapCellData {
	point: THREE.Vector3;
	normal: { x: number; y: number; z: number };
	energy: number;
	irradianceWm2: number;
	illuminanceLux: number;
	photonCount: number;
	group: 'floor' | 'roof' | 'facade' | 'terrain';
	cellSize: number;
	normalizedEnergy: number;
	cellKey: string;
	photonPositions: THREE.Vector3[];
}

export const PHOTON_HEATMAP_OPACITY = 0.6;
export const PHOTON_HEATMAP_TILE_FILL = 0.95;
export const FLOOR_HEATMAP_VERTICAL_TOLERANCE = 0.2;
export const FLOOR_HEATMAP_NEGATIVE_TOLERANCE = 0.02;

export const createHeatmapLegendStats = (
	cellSize: number,
	normalizationMode: PhotonHeatmapNormalizationMode,
	overrides?: Partial<PhotonHeatmapLegendStats>
): PhotonHeatmapLegendStats => ({
	enabled: false,
	cellCount: 0,
	minEnergy: 0,
	maxEnergy: 0,
	cellSize,
	normalizationMode,
	displayMinEnergy: 0,
	displayMaxEnergy: 0,
	...overrides,
});

export const getFloorHeatmapHashKey = (point: THREE.Vector3, cellSize: number): string => {
	const ix = Math.floor(point.x / cellSize);
	const iy = Math.floor(point.y / cellSize);
	const iz = Math.floor(point.z / cellSize);
	return `${ix},${iy},${iz}`;
};

export const getNearbyFloorHashKeys = (
	point: THREE.Vector3,
	cellSize: number,
	verticalBucketRadius: number
): string[] => {
	const ix = Math.floor(point.x / cellSize);
	const iy = Math.floor(point.y / cellSize);
	const iz = Math.floor(point.z / cellSize);
	const keys: string[] = [];
	for (let dx = -1; dx <= 1; dx++) {
		for (let dy = -1; dy <= 1; dy++) {
			for (let dz = -verticalBucketRadius; dz <= verticalBucketRadius; dz++) {
				keys.push(`${ix + dx},${iy + dy},${iz + dz}`);
			}
		}
	}
	return keys;
};

export const getPercentile = (sortedValues: number[], percentile: number): number => {
	if (sortedValues.length === 0) return 0;
	const position = (sortedValues.length - 1) * percentile;
	const lower = Math.floor(position);
	const upper = Math.ceil(position);
	if (lower === upper) return sortedValues[lower];
	const weight = position - lower;
	return sortedValues[lower] * (1 - weight) + sortedValues[upper] * weight;
};

export const getHeatmapColor = (t: number): THREE.Color => {
	const clamped = Math.max(0, Math.min(1, t));
	const hue = (1 - clamped) * 0.66;
	return new THREE.Color().setHSL(hue, 0.95, 0.5);
};
